import { Coordinates } from '@tock/coretypes';
import { FunctionComponent, useEffect, useState } from 'react';
import { IconLocationFilled } from '@tock/icons';
import { Theme, css } from '@emotion/react';
import { responsiblyGetCurrentPosition } from 'utils/locationUtils';
import { useMountEffect } from '@tock/hooks';
import ControlButton from 'components/profile/culinary-travel-log/ControlButton';
import ControlPanel from 'components/profile/culinary-travel-log/ControlPanel';

export interface Props {
  currentLocation?: Coordinates;
  latOffsetFactor?: number;
  map: google.maps.Map;
  onLocation?(location?: Coordinates): void;
  className?: string;
}

const CurrentLocationControl: FunctionComponent<Props> = ({
  currentLocation,
  latOffsetFactor = 0,
  map,
  onLocation,
  className,
}) => {
  const [centered, setCentered] = useState(false);
  const [locating, setLocating] = useState(false);

  const panZoomToLocation = (map: google.maps.Map, coordinates: Coordinates, zoomLevel = 14) => {
    map.setZoom(zoomLevel);

    const bounds = map.getBounds();
    let latOffset = 0;
    if (bounds) {
      const neLat = bounds.getNorthEast().lat();
      const swLat = bounds.getSouthWest().lat();
      latOffset = (neLat - swLat) * latOffsetFactor;
    }
    map.panTo({ lat: coordinates.latitude - latOffset, lng: coordinates.longitude });
    setTimeout(() => setCentered(true), 50);
  };

  const getPosition = (resultOfUserAction = false) => {
    if (resultOfUserAction) {
      setLocating(true);
    }

    responsiblyGetCurrentPosition(resultOfUserAction).then((coordinates) => {
      setLocating(false);
      if (onLocation) {
        onLocation(coordinates);
      }
      if (coordinates && resultOfUserAction) {
        panZoomToLocation(map, coordinates);
      }
    });
  };
  useMountEffect(() => {
    getPosition(false);
  });
  useEffect(() => {
    const listener = map.addListener('center_changed', () => {
      if (centered) {
        setCentered(false);
      }
    });
    return function cleanup() {
      google.maps.event.removeListener(listener);
    };
  }, [map, centered]);
  const onClick = () => {
    if (currentLocation) {
      panZoomToLocation(map, currentLocation);
      if (window.analytics) {
        window.analytics.sendEvent(
          'Citywide search',
          'Citywide search current location control clicked',
          'Recenter Click'
        );
      }
    } else {
      getPosition(true);
      if (window.analytics) {
        window.analytics.sendEvent(
          'Citywide search',
          'Citywide search current location control clicked',
          'Initial Click'
        );
      }
    }
  };

  return (
    <ControlPanel className={className}>
      <ControlButton
        disabled={locating}
        onClick={onClick}
        title="Current location"
        dataTestId="control-button"
      >
        <IconLocationFilled
          css={(theme: Theme) =>
            centered
              ? css`
                  color: ${theme.colors.fgPrimaryBase};
                `
              : null
          }
        />
      </ControlButton>
    </ControlPanel>
  );
};

export default CurrentLocationControl;
